import { BarChart3, ChevronDown, Loader2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { BaseLayout } from '@/shared/components/layout';
import { ErrorState } from '@/shared/errors/ErrorState';
import { useCurrentProject } from '@/shared/hooks/useCurrentProject';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';

import { useSafetyIssues, useSafetyLayouts } from './hooks';
import {
  filterSafetyIssues,
  sortSafetyIssues,
  type SafetyIssueListStatusFilter,
  type SafetyIssuesListFilters,
  type SafetyIssuesSortKey,
  type SafetyIssuesSortState,
} from './listState';
import { SafetyIssuesFilters } from './SafetyIssuesFilters';
import { SafetyIssueSummary } from './SafetyIssueSummary';
import { SafetyIssuesTable } from './SafetyIssuesTable';
import { SafetyViewTabs } from './SafetyViewTabs';
import { useSafetySummaryVisibility } from './useSafetySummaryVisibility';

export const SafetyIssuesPage = () => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { projectIdNumber: projectId } = useCurrentProject();
  const [search, setSearch] = useState('');
  const [layoutId, setLayoutId] = useState('ALL');
  const [locationId, setLocationId] = useState('ALL');
  const [tradeId, setTradeId] = useState('ALL');
  const [sort, setSort] = useState<SafetyIssuesSortState[]>([]);
  const { isSummaryVisible, toggleSummary } = useSafetySummaryVisibility();
  const issues = useSafetyIssues(projectId);
  const layouts = useSafetyLayouts(projectId);
  const safetyPath = location.pathname.replace(/\/issues\/?$/, '');
  const statusParam = searchParams.get('status');
  const status: SafetyIssueListStatusFilter =
    statusParam === 'OPEN' || statusParam === 'RESOLVED' ? statusParam : 'ALL';
  const listFilters = useMemo<SafetyIssuesListFilters>(
    () => ({ search, status, layoutId, locationId, tradeId }),
    [layoutId, locationId, search, status, tradeId]
  );
  const layoutNames = useMemo(
    () => new Map((layouts.data ?? []).map((layout) => [layout.id, layout.name])),
    [layouts.data]
  );
  const openIssueCount = useMemo(
    () => (issues.data ?? []).filter((issue) => issue.status === 'OPEN').length,
    [issues.data]
  );
  const resolvedIssueCount = useMemo(
    () => (issues.data ?? []).filter((issue) => issue.status === 'RESOLVED').length,
    [issues.data]
  );
  const filteredIssues = useMemo(
    () => filterSafetyIssues(issues.data ?? [], listFilters, layoutNames),
    [issues.data, layoutNames, listFilters]
  );
  const sortedIssues = useMemo(
    () => sortSafetyIssues(filteredIssues, sort, layoutNames),
    [filteredIssues, layoutNames, sort]
  );

  const handleFiltersChange = (nextFilters: SafetyIssuesListFilters) => {
    setSearch(nextFilters.search);
    setLayoutId(nextFilters.layoutId);
    setLocationId(nextFilters.locationId);
    setTradeId(nextFilters.tradeId);

    const nextSearchParams = new URLSearchParams(searchParams);
    if (nextFilters.status === 'ALL') {
      nextSearchParams.delete('status');
    } else {
      nextSearchParams.set('status', nextFilters.status);
    }
    setSearchParams(nextSearchParams, { replace: true });
  };

  const handleColumnSortChange = (key: SafetyIssuesSortKey) => {
    setSort((current) => {
      const primary = current[0];
      if (primary?.key === key) {
        return [
          { ...primary, direction: primary.direction === 'asc' ? 'desc' : 'asc' },
          ...current.slice(1),
        ];
      }
      return [{ key, direction: 'asc' }];
    });
  };

  if (!projectId) {
    return (
      <BaseLayout>
        <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6">
          <ErrorState title="Select a project" message="Safety issues are scoped to a project." />
        </div>
      </BaseLayout>
    );
  }

  return (
    <BaseLayout>
      <div className="mx-auto w-full max-w-[1800px] px-4 py-6 sm:px-6 lg:px-9 lg:py-8">
        <div className="mb-[22px] min-w-0">
          <h1 className="font-display text-3xl font-extrabold tracking-[-0.02em] md:text-[2.4rem]">
            Safety
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Track and resolve project safety issues.
          </p>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="-ml-3 mt-1 h-8 gap-2 px-3 text-muted-foreground hover:text-foreground"
            aria-expanded={isSummaryVisible}
            aria-controls="safety-summary"
            onClick={toggleSummary}
          >
            <BarChart3 className="size-4" />
            {isSummaryVisible ? 'Hide summary' : 'Show summary'}
            <ChevronDown
              className={`size-4 transition-transform ${isSummaryVisible ? 'rotate-180' : ''}`}
            />
          </Button>
        </div>

        {isSummaryVisible && !issues.isLoading && !issues.isError && (
          <div id="safety-summary" className="mb-4">
            <SafetyIssueSummary
              openCount={openIssueCount}
              openIssuesPath={`${location.pathname}?status=OPEN`}
              resolvedCount={resolvedIssueCount}
              issues={issues.data ?? []}
            />
          </div>
        )}

        <div className="mb-6">
          <SafetyViewTabs active="list" mapPath={safetyPath} listPath={location.pathname} />
        </div>

        {layouts.isError || issues.isError ? (
          <ErrorState
            title="Safety issues could not be loaded"
            error={layouts.error ?? issues.error}
            onRetry={() => {
              void layouts.refetch();
              void issues.refetch();
            }}
          />
        ) : layouts.isLoading || issues.isLoading ? (
          <Card className="grid min-h-[420px] place-items-center">
            <Loader2 className="size-7 animate-spin text-primary" />
          </Card>
        ) : (
          <div className="min-w-0 space-y-6">
            <SafetyIssuesFilters
              filters={listFilters}
              issues={issues.data ?? []}
              layouts={layouts.data ?? []}
              onFiltersChange={handleFiltersChange}
              onSortChange={setSort}
              sort={sort}
            />

            <SafetyIssuesTable
              issues={sortedIssues}
              layoutNames={layoutNames}
              onSortChange={handleColumnSortChange}
              safetyPath={safetyPath}
              sort={sort}
            />
          </div>
        )}
      </div>
    </BaseLayout>
  );
};
